/**
 * Form Validation Utility
 * Provides reusable form validation logic and error display
 */

/**
 * Get CSS classes for input field based on validation state
 * @param {boolean} attemptedSubmit - Whether form submission was attempted
 * @param {string} fieldName - Name of the field
 * @param {object} validationErrors - Object containing validation errors
 * @param {string} baseClasses - Base CSS classes for the input
 * @returns {string} Complete CSS class string
 */
export const getInputClassName = (attemptedSubmit, fieldName, validationErrors, baseClasses = '') => {
    const hasError = attemptedSubmit && validationErrors[fieldName];
    
    const errorClasses = hasError
        ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500';
    
    return `${baseClasses} ${errorClasses}`.trim();
};

/**
 * Render error message for a field
 * @param {boolean} attemptedSubmit - Whether form submission was attempted
 * @param {string} fieldName - Name of the field
 * @param {object} validationErrors - Object containing validation errors
 * @returns {JSX.Element|null} Error message component or null
 */
export const renderFieldError = (attemptedSubmit, fieldName, validationErrors) => {
    if (!attemptedSubmit || !validationErrors[fieldName]) {
        return null;
    }
    
    return (
        <p className="mt-1 text-sm text-red-600 flex items-center gap-1 animate-shake">
            <span className="font-bold text-base">!</span>
            <span>{validationErrors[fieldName]}</span>
        </p>
    );
};

/**
 * Validate required fields
 * @param {object} formData - Form data object
 * @param {array} requiredFields - Array of required field names or objects
 * @returns {object} Validation errors object
 */
export const validateRequiredFields = (formData, requiredFields) => {
    const errors = {};
    
    requiredFields.forEach(field => {
        const fieldName = typeof field === 'string' ? field : field.name;
        const fieldLabel = typeof field === 'string' ? fieldName : field.label;
        const customValidator = typeof field === 'object' ? field.validator : null;
        
        // Check if field is empty
        if (!formData[fieldName] || formData[fieldName] === '') {
            errors[fieldName] = `${fieldLabel} is required`;
        }
        
        // Run custom validator if provided
        if (customValidator && formData[fieldName]) {
            const customError = customValidator(formData[fieldName]);
            if (customError) {
                errors[fieldName] = customError;
            }
        }
    });
    
    return errors;
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateEmail = (email) => {
    if (!email) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? null : 'Please enter a valid email address';
};

/**
 * Validate phone format
 * @param {string} phone - Phone number to validate
 * @returns {string|null} Error message or null if valid
 */
export const validatePhone = (phone) => {
    if (!phone) return null;
    const phoneRegex = /^[\d\s\-\+\(\)]{10,}$/;
    return phoneRegex.test(phone) ? null : 'Please enter a valid phone number';
};

/**
 * Validate number range
 * @param {number} value - Value to validate
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {string|null} Error message or null if valid
 */
export const validateNumberRange = (value, min, max) => {
    if (value === null || value === undefined || value === '') return null;
    const num = parseFloat(value);
    if (isNaN(num)) return 'Please enter a valid number';
    if (min !== undefined && num < min) return `Value must be at least ${min}`;
    if (max !== undefined && num > max) return `Value must be at most ${max}`;
    return null;
};

/**
 * Add shake animation to CSS (add to your global styles)
 */
export const shakeAnimationCSS = `
@keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
    20%, 40%, 60%, 80% { transform: translateX(4px); }
}

.animate-shake {
    animation: shake 0.5s ease-in-out;
}
`;



